/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { Subscription } from 'aurelia-event-aggregator';
import { SignOnCommand, SignonBase } from 'app/core/pages/signon/signon-base';
import { MsalService } from 'app/shared/services/msal-service';
import { SignOnEvent } from 'app/core/lib/signon-events';

@autoinject()
export class SignonViewModel extends SignonBase {

    private subscriptions: Array<Subscription> = [];

    protected domainHint?: string;

    constructor(
        protected readonly msalService: MsalService,
        ...rest: any[]) {
        super(...rest);
        this.logger.source = this;
    }

    protected attached(): void {
        this.subscriptions.push(
            this.events.subscribe(SignOnEvent, (e: SignOnEvent) => this.handleSignOnEvent(e))
        );

        // Get params
        this.domainHint = this.router.currentInstruction?.queryParams?.domainHint;

        this.signOn();
    }

    protected detached(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    /**
     * Initiate the external Sign On process for a given Sign On Provider
     */
    protected async signOn(): Promise<void> {
        if(!this.signOnProvider) { return; }

        try {
            this.busyState.beginProcessing();

            // TODO[MW]: Move custom sign on methods to their own views.  This page should just be an orchestrator to kick off the appropriate sign on method depending on the provider
            switch(this.signOnProvider.method) {
                case 'oidc-msal':
                    this.statusMessage = this.i18n.tr('Initiating sign on...');
                    await this.msalService.init(this.signOnProvider.key);
                    await this.msalService.loginPopup(this.domainHint);
                    break;
                default:
                    this.logger.error("Unsupported Sign On provider");
                    break;
            }
        }
        catch(error) {
            this.session.clear('error');
            this.errorMessage = 'Sign On Error: Unable to perform the sign on process';
        }
        finally {
            this.busyState.endProcessing();
        }
    }

    protected async handleSignOnEvent(event: SignOnEvent) {
        if(event.isSuccess && this.signingIn && this.signOnProvider) {
            this.signingIn = false;
            this.userName = event.userName;

            const token = this.msalService.getIdToken();
            if(!token) {
                this.logger.error("Missing Token");
                return;
            }

            let cmd: SignOnCommand = {
                provider: {
                    code: this.signOnProvider.key
                },
                application: {
                    code: this.env.config.application
                },
                token: {
                    type: "jwt",
                    value: token
                }
            }

            // Use this configuration setting to force the tenant when it's not coming from the claims
            if(this.signOnProvider.singleTenant) {
                cmd.tenant = {
                    code: this.signOnProvider.singleTenant
                };
            }

            await this.validateSignOn(cmd);
        };
    }

}
